import { useState, useEffect, type FormEvent } from 'react';
import { api, ApiRequestError } from '../../lib/api';

interface ModelProvider {
  id: string;
  display_name: string;
  pool_id: string;
  provider_type: string;
  status: 'enabled' | 'disabled' | 'degraded';
  weight: number;
  rate_limit_rpm: number;
  total_requests: number;
  success_count: number;
  failure_count: number;
  avg_response_time_ms: number;
}

export function AdminModels() {
  const [providers, setProviders] = useState<ModelProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingWeight, setEditingWeight] = useState<{ id: string; weight: number } | null>(null);
  const [editingProvider, setEditingProvider] = useState<ModelProvider | null>(null);
  const [editName, setEditName] = useState('');
  const [editEndpoint, setEditEndpoint] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [editWeight, setEditWeight] = useState(100);
  const [editRateLimit, setEditRateLimit] = useState(60);
  const [testResult, setTestResult] = useState<{
    provider_name: string;
    reachable: boolean;
    message: string;
    request: {
      method: string;
      url: string;
      headers: Record<string, string>;
      body: string | null;
    };
    response: {
      status_code: number;
      status_text: string;
      body: string;
    } | null;
  } | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('gpt-image-2');
  const [formEndpoint, setFormEndpoint] = useState('');
  const [formApiKey, setFormApiKey] = useState('');
  const [formPoolId, setFormPoolId] = useState('');
  const [formWeight, setFormWeight] = useState(100);
  const [formRateLimit, setFormRateLimit] = useState(60);

  const fetchProviders = () => {
    setLoading(true);
    api
      .get<{ providers: ModelProvider[] }>('/admin/models')
      .then((res) => setProviders(res.data?.providers ?? []))
      .catch(() => setError('Failed to load providers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleWeightChange = async (id: string, weight: number) => {
    try {
      await api.patch(`/admin/models/${id}`, { weight });
      setEditingWeight(null);
      fetchProviders();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'enabled' ? 'disabled' : 'enabled';
    try {
      await api.patch(`/admin/models/${id}`, { status: newStatus });
      fetchProviders();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
    }
  };

  const handleTest = async (id: string) => {
    setError('');
    setTestResult(null);
    try {
      const res = await api.post<{
        reachable: boolean;
        message: string;
        display_name: string;
        request: { method: string; url: string; headers: Record<string, string>; body: string | null };
        response: { status_code: number; status_text: string; body: string } | null;
      }>(`/admin/models/${id}/test`);
      setTestResult({
        provider_name: res.data.display_name,
        reachable: res.data.reachable,
        message: res.data.message,
        request: res.data.request,
        response: res.data.response,
      });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(`Test failed: ${err.error.message}`);
      }
    }
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/admin/models', {
        display_name: formName,
        provider_type: formType,
        api_endpoint: formEndpoint,
        credentials_encrypted: formApiKey,
        pool_id: formPoolId || undefined,
        weight: formWeight,
        rate_limit_rpm: formRateLimit,
        supported_sizes: '1024x1024,1536x1024,1024x1536',
        supported_quality_levels: 'low,medium,high',
      });
      setShowCreateForm(false);
      setFormName('');
      setFormEndpoint('');
      setFormApiKey('');
      setFormPoolId('');
      setFormWeight(100);
      setFormRateLimit(60);
      fetchProviders();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
      else setError('Failed to create provider');
    }
  };

  const startEdit = (p: ModelProvider) => {
    setEditingProvider(p);
    setEditName(p.display_name);
    setEditEndpoint(''); // Don't pre-fill, keep existing if empty
    setEditApiKey('');
    setEditWeight(p.weight);
    setEditRateLimit(p.rate_limit_rpm);
  };

  const handleEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingProvider) return;
    setError('');
    try {
      const updates: Record<string, unknown> = {};
      if (editName !== editingProvider.display_name) updates.display_name = editName;
      if (editEndpoint) updates.api_endpoint = editEndpoint;
      if (editApiKey) updates.credentials_encrypted = editApiKey;
      if (editWeight !== editingProvider.weight) updates.weight = editWeight;
      if (editRateLimit !== editingProvider.rate_limit_rpm) updates.rate_limit_rpm = editRateLimit;

      if (Object.keys(updates).length === 0) {
        setEditingProvider(null);
        return;
      }

      await api.patch(`/admin/models/${editingProvider.id}`, updates);
      setEditingProvider(null);
      fetchProviders();
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.error.message);
      else setError('Failed to update provider');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading providers...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Model Provider Management</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]"
        >
          Add Provider
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}

      {testResult && (
        <div className={`rounded-xl border p-6 ${testResult.reachable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${testResult.reachable ? 'text-green-800' : 'text-red-800'}`}>
              Test Result: {testResult.provider_name}
            </h3>
            <button
              onClick={() => setTestResult(null)}
              className="text-gray-400 hover:text-gray-600 text-xl min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <p className={`text-sm font-medium mb-4 ${testResult.reachable ? 'text-green-700' : 'text-red-700'}`}>
            {testResult.message}
          </p>

          {/* Request */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">📤 Request</h4>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto font-mono whitespace-pre-wrap max-h-48 overflow-y-auto">
{`${testResult.request.method} ${testResult.request.url}

Headers:
${Object.entries(testResult.request.headers).map(([k, v]) => `  ${k}: ${v}`).join('\n')}

Body:
${testResult.request.body || '(none)'}`}
            </pre>
          </div>

          {/* Response */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">📥 Response</h4>
            {testResult.response ? (
              <pre className={`p-4 rounded-lg text-xs overflow-x-auto font-mono whitespace-pre-wrap max-h-72 overflow-y-auto ${testResult.reachable ? 'bg-green-900 text-green-100' : 'bg-red-900 text-red-100'}`}>
{`HTTP ${testResult.response.status_code} ${testResult.response.status_text}

Body:
${testResult.response.body || '(empty)'}`}
              </pre>
            ) : (
              <pre className="bg-red-900 text-red-100 p-4 rounded-lg text-xs font-mono">
                No response received (connection failed or timed out)
              </pre>
            )}
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Model Provider</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="e.g. GPT Image Primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider Type</label>
                <select value={formType} onChange={(e) => setFormType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm min-h-[44px]">
                  <option value="gpt-image-2">GPT Image 2</option>
                  <option value="stability-ai">Stability AI</option>
                  <option value="dall-e-3">DALL-E 3</option>
                  <option value="midjourney">Midjourney</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint</label>
                <input type="url" value={formEndpoint} onChange={(e) => setFormEndpoint(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="https://api.openai.com/v1/images" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                <input type="password" value={formApiKey} onChange={(e) => setFormApiKey(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="sk-..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (1-100)</label>
                <input type="number" value={formWeight} onChange={(e) => setFormWeight(Number(e.target.value))} min={1} max={100} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (RPM)</label>
                <input type="number" value={formRateLimit} onChange={(e) => setFormRateLimit(Number(e.target.value))} min={1} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pool ID (optional)</label>
                <input type="text" value={formPoolId} onChange={(e) => setFormPoolId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Leave empty for default" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]">Create Provider</button>
              <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 min-h-[44px]">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {editingProvider && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Provider: {editingProvider.display_name}</h2>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint (leave empty to keep current)</label>
                <input type="url" value={editEndpoint} onChange={(e) => setEditEndpoint(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Leave empty to keep current" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key (leave empty to keep current)</label>
                <input type="password" value={editApiKey} onChange={(e) => setEditApiKey(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Leave empty to keep current" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (1-100)</label>
                <input type="number" value={editWeight} onChange={(e) => setEditWeight(Number(e.target.value))} min={1} max={100} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limit (RPM)</label>
                <input type="number" value={editRateLimit} onChange={(e) => setEditRateLimit(Number(e.target.value))} min={1} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 min-h-[44px]">Save Changes</button>
              <button type="button" onClick={() => setEditingProvider(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 min-h-[44px]">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {providers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No model providers configured</div>
      ) : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left py-3 px-4 font-medium text-gray-600">Provider</th>
              <th className="text-left py-3 px-4 font-medium text-gray-600">Pool</th>
              <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
              <th className="text-center py-3 px-4 font-medium text-gray-600">Weight</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">Requests</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">Success</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">Avg Time</th>
              <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {providers.map((p) => (
              <tr key={p.id}>
                <td className="py-3 px-4 font-medium text-gray-900">{p.display_name}</td>
                <td className="py-3 px-4 text-gray-700">{p.pool_id}</td>
                <td className="py-3 px-4">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      p.status === 'enabled'
                        ? 'bg-green-100 text-green-700'
                        : p.status === 'degraded'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td className="py-3 px-4 text-center">
                  {editingWeight?.id === p.id ? (
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={editingWeight.weight}
                      onChange={(e) =>
                        setEditingWeight({ id: p.id, weight: Number(e.target.value) })
                      }
                      onMouseUp={() => handleWeightChange(p.id, editingWeight.weight)}
                      onTouchEnd={() => handleWeightChange(p.id, editingWeight.weight)}
                      className="w-24"
                    />
                  ) : (
                    <button
                      onClick={() => setEditingWeight({ id: p.id, weight: p.weight })}
                      className="text-blue-600 hover:text-blue-700 min-h-[44px] px-2"
                    >
                      {p.weight}
                    </button>
                  )}
                </td>
                <td className="py-3 px-4 text-right text-gray-700">{p.total_requests}</td>
                <td className="py-3 px-4 text-right text-gray-700">
                  {p.total_requests > 0
                    ? ((p.success_count / p.total_requests) * 100).toFixed(1) + '%'
                    : '-'}
                </td>
                <td className="py-3 px-4 text-right text-gray-700">
                  {p.avg_response_time_ms > 0 ? `${p.avg_response_time_ms}ms` : '-'}
                </td>
                <td className="py-3 px-4 text-right space-x-2">
                  <button
                    onClick={() => startEdit(p)}
                    className="text-xs text-gray-600 hover:text-gray-800 min-h-[44px] px-1"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleTest(p.id)}
                    className="text-xs text-blue-600 hover:text-blue-700 min-h-[44px] px-1"
                  >
                    Test
                  </button>
                  <button
                    onClick={() => handleToggleStatus(p.id, p.status)}
                    className={`text-xs min-h-[44px] px-1 ${
                      p.status === 'enabled'
                        ? 'text-red-600 hover:text-red-700'
                        : 'text-green-600 hover:text-green-700'
                    }`}
                  >
                    {p.status === 'enabled' ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
